import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Sun,
  Wrench,
  Rocket,
  Building2,
  Check,
  Phone,
  Zap,
  Cpu,
  BatteryCharging,
  TrendingUp,
  Award,
} from 'lucide-react';
import CertificationsSection from '../../components/Certifications';
import { useT } from '../../i18n/LanguageContext';

const heroImg =
  'https://images.unsplash.com/photo-1613665813446-82a78c468a1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3ODQ4MTY4NDJ8MA&ixlib=rb-4.1.0&q=85';

const content = {
  fr: {
    back: 'Tous nos services',
    badge: 'SERVICE · ÉNERGIES RENOUVELABLES',
    title: 'Énergies',
    accent: 'renouvelables.',
    hero:
      'Solutions photovoltaïques clé en main, de l’étude au génie civil – toiture, ombrières et centrales au sol pour particuliers, entreprises et collectivités.',
    home: 'Accueil',
    services: 'Services',
    current: 'Énergies renouvelables',

    expertise: 'NOTRE EXPERTISE',
    expertiseTitle: 'Ce que nous réalisons pour vous',

    description:
      'Notre objectif est de vous accompagner, que vous soyez une entreprise ou un particulier, dans la réduction de vos coûts énergétiques mensuels tout en diminuant votre empreinte écologique. En tant qu’entreprise énergétique expérimentée, nous nous efforçons de contribuer à un monde meilleur et plus propre grâce à nos différents services dans le domaine des énergies renouvelables.',

    methodologyTitle: 'Méthodologie & accompagnement',
    methodology: [
      'Études de conception et d’exécution',
      'Modélisation et dimensionnement',
      'Accompagnement, assistance et conseils personnalisés',
      'Installation, maintenance et dépannage',
    ],

    solutionsTitle: 'Nos solutions',
    solutions: [
      'Installations photovoltaïques pour les particuliers ≤ 36 kWc',
      'Installations photovoltaïques moyenne puissance ≤ 500 kWc',
      'Centrales photovoltaïques haute puissance > 500 kWc',
      'Installations en autoconsommation totale ou partielle',
      'Installations avec vente totale ou partielle de la production',
      'Installations hybrides avec batteries de stockage',
    ],

    sectorsTitle: 'Secteurs d’intervention',
    sectors: [
      'Logements / Habitat',
      'Tertiaire / Logistique / Bureaux',
      'Industriel',
      'Collectivités / Mairies',
      'Agricole',
      'Ombrières photovoltaïques',
    ],

    ctaTitle: 'Un projet énergies renouvelables ?',
    ctaText:
      'Obtenez une étude personnalisée et un devis gratuit sous 24h.',
    quote: 'Demander un devis',
    call: 'Nous appeler',

    highlights: [
      { value: '+500', label: 'Installations réalisées' },
      { value: '500 kWc', label: 'Plus grande installation' },
      { value: 'RGE', label: 'Certification QualiPV' },
    ],

    relatedTitle: 'Services complémentaires',
    related: [
      {
        slug: 'courants-forts',
        title: 'Courants forts',
        desc: 'Installations électriques BT/HT tertiaires et industrielles.',
        icon: Zap,
      },
      {
        slug: 'mobilite-electrique',
        title: 'Mobilité électrique',
        desc: 'Conception et déploiement d’infrastructures de recharge (IRVE).',
        icon: BatteryCharging,
      },
      {
        slug: 'courants-faibles',
        title: 'Courants faibles',
        desc: 'Vidéosurveillance, VDI, contrôle d’accès et téléphonie.',
        icon: Cpu,
      },
    ],
  },

  en: {
    back: 'All our services',
    badge: 'SERVICE · RENEWABLE ENERGY',
    title: 'Renewable',
    accent: 'energy.',
    hero:
      'Turnkey photovoltaic solutions, from engineering studies to civil works — rooftop, solar carports and ground-mounted systems for individuals, businesses and public authorities.',
    home: 'Home',
    services: 'Services',
    current: 'Renewable energy',

    expertise: 'OUR EXPERTISE',
    expertiseTitle: 'What we deliver for you',

    description:
      'Our objective is to support businesses and individuals in reducing their monthly energy costs while lowering their environmental footprint. As an experienced energy company, we strive to contribute to a cleaner and better future through our renewable energy solutions.',

    methodologyTitle: 'Methodology & support',
    methodology: [
      'Design and execution studies',
      'Modelling and sizing',
      'Personalised support, assistance and consulting',
      'Installation, maintenance and troubleshooting',
    ],

    solutionsTitle: 'Our solutions',
    solutions: [
      'Residential photovoltaic installations up to 36 kWp',
      'Medium-power photovoltaic installations up to 500 kWp',
      'High-power photovoltaic plants above 500 kWp',
      'Full or partial self-consumption systems',
      'Full or partial electricity production sales systems',
      'Hybrid photovoltaic systems with battery storage',
    ],

    sectorsTitle: 'Industries we serve',
    sectors: [
      'Residential / Housing',
      'Commercial / Logistics / Offices',
      'Industrial',
      'Public authorities / Municipalities',
      'Agricultural',
      'Solar carports',
    ],

    ctaTitle: 'Have a renewable energy project?',
    ctaText:
      'Get a personalised study and a free quotation within 24 hours.',
    quote: 'Request a quotation',
    call: 'Call us',

    highlights: [
      { value: '+500', label: 'Installations completed' },
      { value: '500 kWp', label: 'Largest installation' },
      { value: 'RGE', label: 'QualiPV certification' },
    ],

    relatedTitle: 'Related services',
    related: [
      {
        slug: 'courants-forts',
        title: 'Power systems',
        desc: 'Commercial and industrial LV/MV electrical installations.',
        icon: Zap,
      },
      {
        slug: 'mobilite-electrique',
        title: 'Electric mobility',
        desc: 'Design and deployment of EV charging infrastructure.',
        icon: BatteryCharging,
      },
      {
        slug: 'courants-faibles',
        title: 'Low-voltage systems',
        desc: 'Video surveillance, VDI, access control and telephony.',
        icon: Cpu,
      },
    ],
  },

  ar: {
    back: 'جميع خدماتنا',
    badge: 'الخدمة · الطاقة المتجددة',
    title: 'الطاقة',
    accent: 'المتجددة.',
    hero:
      'حلول متكاملة للطاقة الشمسية، من الدراسات الهندسية إلى الأعمال المدنية، تشمل الأسطح ومواقف السيارات الشمسية والمحطات الأرضية للأفراد والشركات والهيئات المحلية.',
    home: 'الرئيسية',
    services: 'الخدمات',
    current: 'الطاقة المتجددة',

    expertise: 'خبرتنا',
    expertiseTitle: 'ما نقدمه لكم',

    description:
      'هدفنا هو مرافقة الشركات والأفراد في تقليل تكاليف الطاقة الشهرية مع الحد من الأثر البيئي. وبفضل خبرتنا في مجال الطاقة، نسعى إلى المساهمة في بناء مستقبل أنظف وأفضل من خلال حلول الطاقة المتجددة التي نقدمها.',

    methodologyTitle: 'المنهجية والمرافقة',
    methodology: [
      'دراسات التصميم والتنفيذ',
      'النمذجة والحسابات الفنية',
      'المرافقة والمساعدة والاستشارات المخصصة',
      'التركيب والصيانة وإصلاح الأعطال',
    ],

    solutionsTitle: 'حلولنا',
    solutions: [
      'أنظمة الطاقة الشمسية للمنازل حتى 36 كيلوواط ذروة',
      'أنظمة الطاقة الشمسية متوسطة القدرة حتى 500 كيلوواط ذروة',
      'محطات الطاقة الشمسية عالية القدرة بأكثر من 500 كيلوواط ذروة',
      'أنظمة الاستهلاك الذاتي الكامل أو الجزئي',
      'أنظمة بيع كامل أو جزئي لإنتاج الكهرباء',
      'أنظمة هجينة للطاقة الشمسية مع بطاريات تخزين',
    ],

    sectorsTitle: 'قطاعات التدخل',
    sectors: [
      'السكن / المباني السكنية',
      'القطاع التجاري / الخدمات اللوجستية / المكاتب',
      'القطاع الصناعي',
      'الهيئات المحلية / البلديات',
      'القطاع الزراعي',
      'مواقف السيارات الشمسية',
    ],

    ctaTitle: 'هل لديكم مشروع للطاقة المتجددة؟',
    ctaText:
      'احصلوا على دراسة مخصصة وعرض سعر مجاني خلال 24 ساعة.',
    quote: 'طلب عرض سعر',
    call: 'اتصل بنا',

    highlights: [
      { value: '+500', label: 'تركيب تم إنجازه' },
      { value: '500 kWp', label: 'أكبر مشروع تم تنفيذه' },
      { value: 'RGE', label: 'شهادة QualiPV' },
    ],

    relatedTitle: 'الخدمات ذات الصلة',
    related: [
      {
        slug: 'courants-forts',
        title: 'التيارات القوية',
        desc: 'التركيبات الكهربائية للمباني التجارية والصناعية.',
        icon: Zap,
      },
      {
        slug: 'mobilite-electrique',
        title: 'التنقل الكهربائي',
        desc: 'تصميم وتنفيذ البنية التحتية لشحن السيارات الكهربائية.',
        icon: BatteryCharging,
      },
      {
        slug: 'courants-faibles',
        title: 'التيارات الضعيفة',
        desc: 'المراقبة بالفيديو وشبكات VDI والتحكم في الدخول والاتصالات.',
        icon: Cpu,
      },
    ],
  },
};

const CheckItem = ({ children }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-[#0b3b8c]/30 hover:shadow-sm transition-all">
    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
      <Check className="w-3 h-3" strokeWidth={3.5} />
    </span>

    <span className="text-slate-700 text-sm">
      {children}
    </span>
  </div>
);

const SectionTitle = ({ Icon, children }) => (
  <div className="flex items-center gap-3">
    <span className="w-9 h-9 rounded-lg bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center">
      <Icon className="w-4 h-4" strokeWidth={2.4} />
    </span>

    <h3 className="text-[#0b1c3a] font-bold text-lg md:text-xl tracking-tight">
      {children}
    </h3>
  </div>
);

const EnergiesRenouvelables = () => {
  const { lang } = useT();

  const currentLang = ['fr', 'en', 'ar'].includes(lang)
    ? lang
    : 'fr';

  const c = content[currentLang];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      className="bg-white"
      dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
    >

      {/* HERO */}
      <section className="relative pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden">

        <img
          src={heroImg}
          alt={c.current}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1c3a]/95 via-[#0b1c3a]/80 to-[#0b1c3a]/95" />

        <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-[#0b3b8c] blur-[140px] opacity-40 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {c.back}
          </Link>

          <div className="mt-8 flex items-center gap-4">

            <div className="w-12 h-12 rounded-xl bg-[#0b3b8c] flex items-center justify-center border border-white/10">
              <Sun
                className="w-6 h-6 text-white"
                strokeWidth={2.4}
              />
            </div>

            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/90 text-[11px] tracking-[0.25em] font-semibold">
              {c.badge}
            </span>

          </div>

          <h1 className="mt-6 text-white font-black leading-[1.05] tracking-tight text-4xl md:text-6xl lg:text-7xl max-w-4xl">

            {c.title}{' '}

            <span className="italic font-serif text-[#f5c518]">
              {c.accent}
            </span>

          </h1>

          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {c.hero}
          </p>

          <nav
            aria-label="breadcrumb"
            className="mt-8 flex items-center gap-2 text-white/70 text-sm"
          >
            <Link
              to="/"
              className="hover:text-white transition-colors"
            >
              {c.home}
            </Link>

            <span>/</span>

            <Link
              to="/services"
              className="hover:text-white transition-colors"
            >
              {c.services}
            </Link>

            <span>/</span>

            <span className="text-[#f5c518] font-semibold">
              {c.current}
            </span>
          </nav>

        </div>

        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white" />

      </section>

      {/* MAIN */}
      <section className="pb-24">

        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start">

          <div>

            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.expertise}
            </span>

            <h2 className="mt-5 text-3xl md:text-4xl font-black text-[#0b1c3a] leading-tight tracking-tight">
              {c.expertiseTitle}
            </h2>

            <div className="mt-6 p-5 md:p-6 rounded-2xl bg-slate-50 border-l-4 border-[#0b3b8c] text-slate-700 leading-relaxed">
              {c.description}
            </div>

            {/* METHODOLOGY */}
            <div className="mt-10">

              <SectionTitle Icon={Wrench}>
                {c.methodologyTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.methodology.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

            {/* SOLUTIONS */}
            <div className="mt-12">

              <SectionTitle Icon={Rocket}>
                {c.solutionsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.solutions.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

            {/* SECTORS */}
            <div className="mt-12">

              <SectionTitle Icon={Building2}>
                {c.sectorsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.sectors.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

          </div>

          {/* RIGHT SIDEBAR */}
          <aside className="lg:sticky lg:top-28 space-y-6">

            {/* CTA */}
            <div className="relative bg-[#0b1c3a] text-white rounded-3xl p-7 shadow-xl overflow-hidden">

              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#0b3b8c] blur-[70px] opacity-60" />

              <div className="relative">

                <div className="w-11 h-11 rounded-xl bg-[#f5c518] text-[#0b1c3a] flex items-center justify-center">
                  <Sun
                    className="w-5 h-5"
                    strokeWidth={2.4}
                  />
                </div>

                <h3 className="mt-5 text-xl font-bold leading-tight">
                  {c.ctaTitle}
                </h3>

                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {c.ctaText}
                </p>

                <Link
                  to="/contact"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0b3b8c] hover:bg-[#082e6f] font-semibold text-sm transition-colors"
                >
                  {c.quote}
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <a
                  href="tel:+33442072262"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-[#0b1c3a] hover:bg-slate-100 font-semibold text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {c.call}
                </a>

              </div>

            </div>

            {/* HIGHLIGHTS */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6 divide-y divide-slate-100">

              {c.highlights.map((h, i) => {

                const Icon =
                  i === 0
                    ? TrendingUp
                    : i === 1
                    ? Zap
                    : Award;

                return (
                  <div
                    key={h.label}
                    className={
                      i === 0
                        ? 'pb-4'
                        : i === c.highlights.length - 1
                        ? 'pt-4'
                        : 'py-4'
                    }
                  >

                    <div className="flex items-center gap-3">

                      <Icon className="w-5 h-5 text-[#0b3b8c]" />

                      <div>

                        <div className="text-[#0b1c3a] text-2xl font-black leading-none tracking-tight">
                          {h.value}
                        </div>

                        <div className="mt-1 text-slate-500 text-xs">
                          {h.label}
                        </div>

                      </div>

                    </div>

                  </div>
                );
              })}

            </div>

          </aside>

        </div>

      </section>

      {/* CERTIFICATIONS */}
      <CertificationsSection />

      {/* RELATED SERVICES */}
      <section className="py-20 bg-[#f7f8fb] border-t border-slate-200">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <h2 className="text-2xl md:text-3xl font-black text-[#0b1c3a] tracking-tight">
            {c.relatedTitle}
          </h2>

          <div className="mt-8 grid md:grid-cols-3 gap-4">

            {c.related.map((r) => {

              const Icon = r.icon;

              return (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4 hover:shadow-md hover:border-[#0b3b8c]/30 transition-all"
                >

                  <div className="shrink-0 w-11 h-11 rounded-xl bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center group-hover:bg-[#0b3b8c] group-hover:text-white transition-colors">

                    <Icon
                      className="w-5 h-5"
                      strokeWidth={2.2}
                    />

                  </div>

                  <div className="min-w-0 flex-1">

                    <div className="text-[#0b1c3a] font-bold text-sm">
                      {r.title}
                    </div>

                    <div className="text-slate-500 text-xs mt-1 line-clamp-1">
                      {r.desc}
                    </div>

                  </div>

                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#0b3b8c] transition-colors" />

                </Link>
              );
            })}

          </div>

        </div>

      </section>

    </main>
  );
};

export default EnergiesRenouvelables;